import type { Plan, PlanBody, PlanEntry } from "../api/types";

/**
 * Montar y desmontar el plan what-if desde la interfaz — sección 7.5 (M7-T02 · UC-07.02).
 *
 * QUÉ ES UN PLAN AQUÍ
 *   Dos listas de `{ playerId, price }`: lo que estoy pensando comprar y lo que estoy pensando
 *   vender. El servidor guarda esas listas tal cual y devuelve el veredicto (`planValid`,
 *   `reasons`). Estas funciones son solo las tijeras y el pegamento de los botones *Añadir al
 *   plan* y *Quitar del plan* (sección 8.3).
 *
 * POR QUÉ NO MUTAN NADA
 *   El plan que se edita viene de la caché de TanStack Query, y escribir dentro de un objeto de la
 *   caché lo cambia sin que React se entere de que hay que repintar. Cada función devuelve un plan
 *   nuevo; quien lo tenga se lo pasa a `useUpdatePlan`.
 *
 * QUÉ NO SE COMPRUEBA AQUÍ
 *   Si el plan se sostiene. Los cuatro motivos de invalidez de la sección 7.5 —vender a quien no
 *   es mío, presupuesto negativo, exceso de plantilla, repetidos— los calcula el servidor, que es
 *   el que conoce mi plantilla y mi dinero. Lo que sí se evita es lo que no significa nada: el
 *   mismo jugador dos veces, o comprarlo y venderlo a la vez.
 */

/** Los dos lados del plan, con el mismo nombre que en la sección 9.2. */
export type PlanSide = "buys" | "sells";

/** El plan sin el veredicto: lo único que se edita y lo único que viaja en `PUT /api/plan`. */
export type PlanDraft = PlanBody;

/** Un plan recién empezado. */
export function emptyPlan(): PlanDraft {
  return { buys: [], sells: [] };
}

/** El lado contrario: comprar y vender al mismo jugador no describe ninguna intención. */
function otherSide(side: PlanSide): PlanSide {
  return side === "buys" ? "sells" : "buys";
}

/**
 * Quita el veredicto y deja solo lo editable. Con `undefined` —la consulta aún no ha respondido—
 * devuelve un plan vacío, para que la pantalla pueda ofrecer los botones desde el primer momento.
 */
export function toPlanDraft(plan: Plan | PlanDraft | undefined): PlanDraft {
  return {
    buys: [...(plan?.buys ?? [])],
    sells: [...(plan?.sells ?? [])],
  };
}

/** `true` si ese jugador está en ese lado del plan. */
export function isInPlan(
  plan: PlanDraft,
  side: PlanSide,
  playerId: number,
): boolean {
  return plan[side].some((entry) => entry.playerId === playerId);
}

/** En qué lado está el jugador, o `null` si no está en el plan. */
export function planSideOf(plan: PlanDraft, playerId: number): PlanSide | null {
  if (isInPlan(plan, "buys", playerId)) return "buys";
  if (isInPlan(plan, "sells", playerId)) return "sells";
  return null;
}

/**
 * Añade —o actualiza— una entrada. Si el jugador ya estaba en ese lado se **sustituye el precio**
 * en su sitio, porque el mercado se mueve y volver a añadirlo significa «al precio de ahora», no
 * «dos veces». Si estaba en el lado contrario, sale de él.
 */
export function addToPlan(
  plan: PlanDraft,
  side: PlanSide,
  entry: PlanEntry,
): PlanDraft {
  const actual = plan[side];
  const lado = isInPlan(plan, side, entry.playerId)
    ? actual.map((otra) =>
        otra.playerId === entry.playerId ? { ...entry } : otra,
      )
    : [...actual, { ...entry }];
  const resto = plan[otherSide(side)].filter(
    (otra) => otra.playerId !== entry.playerId,
  );

  return side === "buys"
    ? { buys: lado, sells: resto }
    : { buys: resto, sells: lado };
}

/** Quita un jugador de un lado del plan. Si no estaba, el plan sale igual. */
export function removeFromPlan(
  plan: PlanDraft,
  side: PlanSide,
  playerId: number,
): PlanDraft {
  const lado = plan[side].filter((entry) => entry.playerId !== playerId);

  return side === "buys"
    ? { buys: lado, sells: [...plan.sells] }
    : { buys: [...plan.buys], sells: lado };
}

/**
 * Lo que hace un botón que dice *Añadir al plan* o *Quitar del plan* según cómo esté: si el
 * jugador ya está en ese lado lo saca, y si no, lo mete.
 */
export function togglePlanEntry(
  plan: PlanDraft,
  side: PlanSide,
  entry: PlanEntry,
): PlanDraft {
  return isInPlan(plan, side, entry.playerId)
    ? removeFromPlan(plan, side, entry.playerId)
    : addToPlan(plan, side, entry);
}

/** Cuántas entradas hay en cada lado: lo que enseña el resumen del plan. */
export function planCounts(plan: PlanDraft): { buys: number; sells: number } {
  return { buys: plan.buys.length, sells: plan.sells.length };
}

/** `true` si no hay nada que ejecutar: ni compras ni ventas. */
export function isEmptyPlan(plan: PlanDraft): boolean {
  return plan.buys.length === 0 && plan.sells.length === 0;
}
